import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  BellRing,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  FileText,
  Plus,
  Repeat,
  Send,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ExportCsvButton,
  InfoHint,
  PageHeader,
  SearchInput,
  SegmentedControl,
  Select,
  SkeletonTable,
  StatCard,
  StatusPill,
  type StatKind,
  invoiceStatusToPill,
  sortRows,
  toast,
  type DataTableColumn,
  type DataTableSort,
} from '../../components/ui';
import { cn } from '../../lib/cn';
import { csvFilename, csvMoney, downloadCsv } from '../../lib/csv';
import { formatDate, formatMonth, formatMoney } from '../../lib/format';
import { invoiceNumberSortKey } from './invoice-number';
import { BulkActionBar, RowCheckbox } from './bulk';
import { DepositsNotice } from '../bank/DepositsNotice';
import { agoLabel, chaseLabel, followUpLabel, sentLabel } from './chase';
import { ChaseReminderBar } from './ChaseReminderBar';
import {
  describeError,
  useInvoiceClients,
  useInvoiceSummary,
  useInvoices,
  useDeliveryStatus,
  useUnbilled,
  type InvoiceListItem,
  type InvoiceSummary,
} from './api';
import { RecordPaymentModal } from './payment-modal';

import { RECURRING_TAB, STATE_TABS, STRIP_TABS, type StateTab } from './state-tabs';
import { rememberInvoiceListView } from './list-view';
import { formatScheduledShort } from './ScheduleSendDialog';

/**
 * The list opens on a work queue, not a ledger.
 *
 * It used to default to All: 156 rows, four pages, overwhelmingly PAID — a
 * receipt archive standing where "who owes me money" should be. OPEN is
 * everything still owed (open + partly paid, due or overdue), which is what
 * the Outstanding tile above it counts, and the tabs narrow from there. The
 * archive is one click away and every state is still a shareable URL.
 */
const DEFAULT_TAB: StateTab = 'OPEN';

type AgingKey = keyof InvoiceSummary['aging'];

const AGING_SEGMENTS: Array<{
  key: AgingKey;
  label: string;
  className: string;
  /** Overdue-day window this bucket covers (inclusive). */
  min: number;
  max: number;
}> = [
  { key: 'current', label: 'Current', className: 'bg-accent', min: -Infinity, max: 0 },
  { key: 'days1_30', label: '1–30', className: 'bg-warning', min: 1, max: 30 },
  { key: 'days31_60', label: '31–60', className: 'bg-orange-600', min: 31, max: 60 },
  { key: 'days61_90', label: '61–90', className: 'bg-status-late', min: 61, max: 90 },
  { key: 'days90plus', label: '90+', className: 'bg-primary-900', min: 91, max: Infinity },
];

/**
 * The chase queues. `never` and `due` are disjoint — a late invoice needs a
 * FIRST chase or its NEXT one, never both — and `today` is the two of them
 * together, which is the number a morning actually starts from.
 */
type ChaseQueue = 'today' | 'never' | 'due';
const CHASE_QUEUES = new Set<string>(['today', 'never', 'due']);

/** Client-side cross-cut of the fetched list, driven by the KPI tiles + chips. */
type Focus =
  | { kind: 'outstanding' }
  | { kind: 'aging'; bucket: AgingKey }
  | { kind: 'chase'; queue: ChaseQueue }
  | null;

const PAGE_SIZE = 50;

/**
 * Rollups on this page are whole dollars, because that is what they are
 * everywhere else. The dashboard's AR tile, /clients' Open AR column and a
 * client's Open AR tile all print $205,834; this page printed $205,833.75 for
 * the same figure one click later, which reads as two different numbers rather
 * than one number rendered twice.
 *
 * The rule is about the KIND of figure, not the screen: a TOTAL across a set of
 * invoices is whole; an amount somebody pays exactly — an invoice's amount, its
 * amount due, a payment — keeps its cents, and the table below still does.
 */
const ROLLUP = { whole: true } as const;

const VALID_STATES = new Set<string>(STATE_TABS.map((t) => t.value));
const AGING_KEYS = new Set(AGING_SEGMENTS.map((s) => s.key as string));

/** `?focus=outstanding` / `?focus=aging:days90plus` / `?focus=chase:due` → Focus. */
function parseFocus(raw: string | null): Focus {
  if (!raw) return null;
  if (raw === 'outstanding') return { kind: 'outstanding' };
  const [kind, value] = raw.split(':');
  if (kind === 'aging' && AGING_KEYS.has(value)) return { kind: 'aging', bucket: value as AgingKey };
  if (kind === 'chase' && CHASE_QUEUES.has(value)) {
    return { kind: 'chase', queue: value as ChaseQueue };
  }
  return null;
}

function serializeFocus(focus: Focus): string | null {
  if (!focus) return null;
  if (focus.kind === 'outstanding') return 'outstanding';
  if (focus.kind === 'chase') return `chase:${focus.queue}`;
  return `aging:${focus.bucket}`;
}

/**
 * Late views lead with the oldest debt, not the newest invoice: filtering to
 * Late used to bury a 70-day, $20k invoice at the bottom of the list.
 */
function isLateView(stateTab: string, focus: Focus): boolean {
  if (stateTab === 'LATE') return true;
  if (focus?.kind === 'chase') return true;
  return focus?.kind === 'aging' && focus.bucket !== 'current';
}

/**
 * ⚠ THE DEFAULT VIEW LEADS WITH THE LATE MONEY.
 *
 * Open used to sort newest-issued first, which is the one order that buries
 * the answer: the newest invoices are the ones nobody owes yet, so at 1440×900
 * the first screenful of a page subtitled "who owes what, and how late it is"
 * was five *current* invoices and not one overdue row. Oldest due date first
 * puts every late invoice above the fold and the not-yet-due tail below it,
 * which is the same order the Late tab uses and the same order a morning is
 * actually worked in.
 */
function defaultSortFor(stateTab: string, focus: Focus): DataTableSort {
  if (isLateView(stateTab, focus)) return { key: 'dueDate', dir: 'asc' };
  if (stateTab === 'OPEN' || stateTab === 'PARTIAL') return { key: 'dueDate', dir: 'asc' };
  return { key: 'issueDate', dir: 'desc' };
}

/**
 * Columns whose FIRST click must sort descending. Clicking AMOUNT DUE and
 * getting the smallest numbers — a page of paid invoices showing "—" — is not
 * a sort, it is a punishment. Money and staleness are read biggest-first.
 */
const DESC_FIRST = new Set(['amount', 'dueAmount', 'lastChased']);

/**
 * Turn the table's next-sort into a URL value, flipping the FIRST click on a
 * money/staleness column to descending. The table toggles from ascending by
 * default, which is right for names and dates and wrong for dollars.
 */
function serializeSort(next: DataTableSort, current: DataTableSort): string | null {
  if (!next) return null;
  const firstClick = current?.key !== next.key;
  const dir = firstClick && DESC_FIRST.has(next.key) ? 'desc' : next.dir;
  return `${next.key}:${dir}`;
}

function isOutstanding(r: InvoiceListItem): boolean {
  return r.state === 'OPEN' || r.state === 'PARTIAL';
}

function matchesFocus(r: InvoiceListItem, focus: Focus): boolean {
  if (!focus) return true;
  if (!isOutstanding(r)) return false;
  if (focus.kind === 'outstanding') return true;
  if (focus.kind === 'chase') {
    if (focus.queue === 'today') return r.chase.needsChase;
    return focus.queue === 'never' ? r.chase.neverChased : r.chase.dueForFollowUp;
  }
  const seg = AGING_SEGMENTS.find((s) => s.key === focus.bucket);
  if (!seg) return true;
  const days = r.isLate ? r.daysOverdue : 0;
  return days >= seg.min && days <= seg.max;
}

function focusLabel(focus: Focus): string {
  if (!focus) return '';
  if (focus.kind === 'outstanding') return 'Outstanding invoices';
  if (focus.kind === 'chase') {
    return focus.queue === 'today'
      ? 'Chase · to chase today'
      : focus.queue === 'never'
        ? 'Chase · never chased'
        : 'Chase · due to follow up';
  }
  const seg = AGING_SEGMENTS.find((s) => s.key === focus.bucket);
  return seg ? `AR aging · ${seg.label}${seg.key === 'current' ? '' : ' days late'}` : 'AR aging';
}

// ---------------------------------------------------------------------------
// KPI tiles — the shared StatCard, in its navigational form
// ---------------------------------------------------------------------------

/**
 * These used to be a private copy of `StatCard` ("same look but actually
 * navigational"), which is how /invoices ended up with a different tile
 * padding, value size and hint behaviour from every other money screen. The
 * shared primitive now carries `to`/`onClick`/`active`, so this is a thin
 * spelling of it — the only local part left is the phone type-scale, which
 * belongs to this page's five-tile-plus-aging-card strip.
 */
function KpiTile({
  label,
  kind,
  value,
  hint,
  onClick,
  to,
  active = false,
  loading = false,
  error = false,
  errorMessage,
  onRetry,
  tone,
}: {
  label: string;
  kind: StatKind;
  value: string;
  hint?: string;
  onClick?: () => void;
  to?: string;
  active?: boolean;
  loading?: boolean;
  /**
   * ⚠ **THESE TILES ARE FED BY THEIR OWN REQUESTS** (`/invoices/summary` and
   * `/invoices/unbilled`), which can fail while the table under them loads
   * perfectly — and then every one of them printed "—", the same glyph this
   * app uses for *no data*, so a failed AR total read as "you are owed
   * nothing". The table's error branch could not cover them because it belongs
   * to a different query. Same fix as /team and /dashboard.
   */
  error?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  tone?: 'late';
}) {
  return (
    <StatCard
      label={label}
      kind={kind}
      value={value}
      hint={hint}
      hintLines={2}
      loading={loading}
      error={error}
      errorMessage={errorMessage}
      onRetry={onRetry}
      to={to}
      onClick={onClick}
      active={active}
      valueClassName={cn('text-heading-3 sm:text-heading-2', tone === 'late' && 'text-status-late')}
    />
  );
}

/**
 * ⚠ A STRIP, NOT A CARD — and it shares its row with the chase chips.
 *
 * This page is a table. It was giving the table a third of the screen: at
 * 1440×900 the first invoice sat at y=594 and 5 of 15 open invoices were
 * visible, because a full-width AR-aging CARD stood between the KPI tiles and
 * the filters, restating a figure the OVERDUE tile above it already printed
 * ($120,985 = the four non-current buckets = "7 late" on the chase strip
 * below). Nothing here was deleted — the bar, all five buckets with their
 * amounts, the click-to-filter and the two-sided-rule sentence are all still
 * on screen — it is one line of chips instead of a 130px card, on the row the
 * chase chips already occupied.
 */
function AgingStrip({
  summary,
  loading,
  focus,
  onSelect,
  className,
  /**
   * On a late-only view the Current band is not just uninteresting, it is
   * WRONG to show: the Late tab printed "Current $84,849" beside a table
   * containing no current invoice, and the segments then summed to a number
   * the page was not about.
   */
  lateOnly = false,
}: {
  summary?: InvoiceSummary;
  loading: boolean;
  focus: Focus;
  onSelect: (bucket: AgingKey) => void;
  className?: string;
  lateOnly?: boolean;
}) {
  const segments = lateOnly
    ? AGING_SEGMENTS.filter((s) => s.key !== 'current')
    : AGING_SEGMENTS;
  const total = summary
    ? segments.reduce((s, seg) => s + summary.aging[seg.key].amount, 0)
    : 0;
  return (
    <div className={cn('flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1', className)}>
      <p className="shrink-0 text-caption font-semibold uppercase tracking-wider text-grey-500">
        AR aging{lateOnly ? ' · overdue only' : ''}
      </p>
      {loading || !summary ? (
        <div className="h-2.5 w-40 animate-pulse-soft rounded-full bg-grey-200" />
      ) : total <= 0 ? (
        <p className="text-body-sm text-grey-500">Nothing outstanding — all caught up.</p>
      ) : (
        <>
          <div
            className="flex h-2.5 w-24 shrink-0 overflow-hidden rounded-full bg-grey-100"
            role="img"
            aria-label={`Accounts receivable aging, ${formatMoney(total, ROLLUP)} total`}
          >
            {segments.map((seg) => {
              const amount = summary.aging[seg.key].amount;
              if (amount <= 0) return null;
              return (
                <div
                  key={seg.key}
                  className={seg.className}
                  style={{ width: `${(amount / total) * 100}%` }}
                  title={`${seg.label}: ${formatMoney(amount, ROLLUP)}`}
                />
              );
            })}
          </div>
          {/* Chips wrap instead of scrolling sideways: the horizontal scroller
              left "1–30 $64,015." clipped mid-figure at rest, and a truncated
              money value is worse than an omitted one. Wrapping packs two per
              row at 390px with every figure whole. */}
          <div className="flex flex-wrap gap-x-2 gap-y-1">
            {segments.map((seg) => {
              const bucket = summary.aging[seg.key];
              const active = focus?.kind === 'aging' && focus.bucket === seg.key;
              return (
                <button
                  key={seg.key}
                  type="button"
                  onClick={() => onSelect(seg.key)}
                  aria-pressed={active}
                  disabled={bucket.count === 0}
                  title={
                    bucket.count === 0
                      ? `No invoices in ${seg.label}`
                      : `Show the ${bucket.count} invoice${bucket.count === 1 ? '' : 's'} in ${seg.label}`
                  }
                  className={cn(
                    // 11px on phones is what lets two whole money figures share
                    // a 390px row instead of one bucket per line.
                    'flex min-w-0 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-1 py-0.5 text-[11px] leading-5 text-grey-600 transition-colors sm:px-1.5 sm:text-caption',
                    bucket.count === 0
                      ? 'cursor-default opacity-45'
                      : 'hover:bg-grey-100 hover:text-secondary',
                    active && 'bg-secondary text-white hover:bg-secondary hover:text-white',
                  )}
                >
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', seg.className)} aria-hidden />
                  <span className="font-semibold">{seg.label}</span>
                  <span className="num">{formatMoney(bucket.amount, ROLLUP)}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * ⚠ THE OTHER HALF OF A TWO-SIDED RULE, AND IT MAY NOT BE LOST.
 *
 * This page ages an overdue invoice BACKWARDS by due date — "how late is this
 * money?". The cash forecast rolls the SAME invoice FORWARD into the current
 * half-month period — "when will it arrive?". Both are right; an unlabelled
 * "$120,985" two clicks apart that means two different things is the failure
 * this exists to prevent. /budget-forecast carries the mirror.
 *
 * ⚠ **A LABEL, NOT A LESSON** (Jesse, 2026-08-13: *"if this description needs
 * to be available make it in (i) icon info popups"*). It was a two-line
 * paragraph above the table. What a reader needs in order not to MISREAD the
 * figures is two clauses — the money is billed, not yet revenue, and it is aged
 * backwards. Those stay on screen. The reconciliation (what makes it revenue,
 * what the forecast does with the same invoices, and the link there) moved into
 * the (i) — **whole**. Deleting either half is a regression; so is putting the
 * paragraph back on the page.
 */
function AgingBasisNote() {
  return (
    <p className="text-caption text-grey-500">
      {/* ⚠ THE BASIS CLAUSE. Revenue in this app is CASH — money counts when it
          arrives — so everything on this page is money that has been BILLED and
          has not become revenue yet. Without this clause a reader lands here
          from a $1.0M revenue figure and adds $205,834 to it. */}
      <span className="font-semibold text-grey-600">Billed, not yet revenue</span> · aged{' '}
      <span className="font-semibold text-grey-600">backwards</span> by due date
      <InfoHint label="What these figures are" className="ml-1" width={330}>
        <p>
          <strong>Billed, not yet revenue.</strong> Revenue in this app is cash — a{' '}
          <strong>payment</strong> is what makes it revenue, so nothing on this page has counted
          as income yet.
        </p>
        <p className="mt-2">
          <strong>Aged backwards.</strong> An overdue invoice is bucketed by how many days past
          its due date it is — <em>how late is this money</em>.
        </p>
        <p className="mt-2">
          <Link to="/budget-forecast" className="font-semibold text-accent hover:underline">
            Budget &amp; Forecast
          </Link>{' '}
          rolls the <strong>same</strong> invoices <strong>forward</strong> into the current
          period — <em>when will the money arrive</em>. Both are right; they answer different
          questions, so the same $120,985 appears in both places meaning two different things.
        </p>
      </InfoHint>
    </p>
  );
}

/** One keycap in the shortcut legend — a `kbd`, so a screen reader says so. */
function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="num rounded border border-grey-200 bg-grey-50 px-1 py-px text-caption font-semibold text-grey-600">
      {children}
    </kbd>
  );
}

// ---------------------------------------------------------------------------
// Chasing — "who do I chase this morning?", answered above the table
// ---------------------------------------------------------------------------

/**
 * A horizontally scrolling row that ADMITS it scrolls.
 *
 * The chase strip already scrolled at 390px, and that was the whole problem:
 * it ended mid-chip ("2 due to follow u…") with no edge, no shadow and no
 * arrow, so the last queue was reachable only by somebody who guessed. A
 * permanent gradient would be worse — on a desktop, where the row fits, it
 * would be a lie — so the fade is MEASURED and appears only while there is
 * something past the edge, on either side.
 */
function ScrollFade({ children, className }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ left: el.scrollLeft > 2, right: max > 2 && el.scrollLeft < max - 2 });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [measure, children]);

  return (
    <div className={cn('relative min-w-0', className)}>
      <div ref={ref} onScroll={measure} className="overflow-x-auto sm:overflow-visible">
        {children}
      </div>
      {edges.left && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent"
        />
      )}
      {edges.right && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 flex w-8 items-center justify-end bg-gradient-to-l from-white via-white/80 to-transparent"
        >
          <ChevronRight className="h-3.5 w-3.5 text-grey-400" />
        </span>
      )}
    </div>
  );
}

/**
 * ⚠ A COLUMN THAT PRINTS ONE PHRASE ON EVERY ROW IS PROSE, NOT DATA.
 *
 * The CLIENT / SUBJECT cell printed "Professional services — Aug 2026" on 12
 * of the 15 open invoices: the phrase is the same on every row, and the month
 * it ends with is already in the ISSUED column two cells to the right. The
 * codebase states this rule about itself (the billed-work table drops its RATE
 * column when all 26 entries share one rate) — this is the same test, applied
 * to the widest column on the busiest screen.
 *
 * Two steps, both conservative:
 *  1. drop a trailing "— <Month Year>" ONLY when it names the invoice's own
 *     issue month, so nothing a human typed is ever discarded;
 *  2. if what remains is shared by MOST rows currently in view, it cannot tell
 *     one row from another here — so the cell says something that can: how the
 *     chase is going, or the PO number the client's AP will quote back.
 *
 * A subject somebody actually wrote ("Phase 2 — discovery") is distinguishing
 * and always survives.
 */
export function trimRedundantMonth(subject: string, issueDate: string): string {
  const month = formatMonth(issueDate);
  const long = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    new Date(`${issueDate}T12:00:00Z`),
  );
  const escaped = [month, long].map((m) => m.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return subject
    .replace(new RegExp(String.raw`\s*[—–-]\s*(${escaped.join('|')})\s*$`, 'i'), '')
    .trim();
}

/** Subjects that appear on more than half the rows in view carry no signal. */
export function boilerplateSubjects(rows: InvoiceListItem[]): Set<string> {
  if (rows.length < 4) return new Set();
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (!r.subject) continue;
    const key = trimRedundantMonth(r.subject, r.issueDate).toLowerCase();
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const out = new Set<string>();
  for (const [key, n] of counts) if (n > rows.length / 2) out.add(key);
  return out;
}

/**
 * The second line of the CLIENT / SUBJECT cell. See `boilerplateSubjects` for
 * why it is not simply `r.subject`.
 */
function SubjectLine({
  row,
  boilerplate,
  showChase,
}: {
  row: InvoiceListItem;
  boilerplate: Set<string>;
  /** False on the late views, where "Last chased" already has a column. */
  showChase: boolean;
}) {
  const trimmed = row.subject ? trimRedundantMonth(row.subject, row.issueDate) : null;
  const uninformative = trimmed !== null && boilerplate.has(trimmed.toLowerCase());

  if (uninformative) {
    // Something that actually tells one row from another. Chase state is the
    // question this screen exists to answer and has NO column of its own on
    // the default view (it earns one only on the late views, where this falls
    // back rather than printing the same fact twice). Then the PO, which the
    // client's AP quotes back and which appears nowhere else here.
    const chase = showChase ? chaseLabel(row.chase, row.isLate) : null;
    if (chase) {
      return (
        <p
          className={cn(
            'truncate text-caption',
            row.chase.needsChase ? 'font-semibold text-status-late' : 'text-grey-500',
          )}
          title={row.subject ?? undefined}
        >
          {chase}
        </p>
      );
    }
    return (
      <p className="truncate text-caption text-grey-400" title={row.subject ?? undefined}>
        {row.poNumber ? <span className="num text-grey-500">PO {row.poNumber}</span> : trimmed}
      </p>
    );
  }

  return (
    <p className="truncate text-caption text-grey-500">
      {trimmed ?? <span className="capitalize">{row.kind}</span>}
      {/* "+2 more" only makes sense when the subject IS a line item. */}
      {row.subjectFromLineItem && row.lineCount && row.lineCount > 1 ? (
        <span className="text-grey-400"> +{row.lineCount - 1} more</span>
      ) : null}
    </p>
  );
}

function ChaseChip({
  label,
  count,
  tone,
  active,
  onClick,
  title,
}: {
  label: string;
  count: number;
  tone: 'late' | 'never' | 'due';
  active: boolean;
  onClick: () => void;
  title: string;
}) {
  const disabled = count === 0;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      title={disabled ? `No invoices ${label}` : title}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-caption font-semibold transition-colors',
        disabled && 'cursor-default border-grey-200 text-grey-400',
        !disabled &&
          !active &&
          (tone === 'never'
            ? 'border-status-late-border bg-status-late-bg text-status-late hover:bg-status-late-bg/70'
            : tone === 'due'
              ? 'border-warning/40 bg-warning/10 text-secondary hover:bg-warning/20'
              : 'border-grey-200 bg-white text-grey-600 hover:border-grey-300 hover:text-secondary'),
        active && 'border-secondary bg-secondary text-white',
      )}
    >
      <span className="num">{count}</span>
      {label}
    </button>
  );
}

/**
 * The LAST CHASED cell: how long since anyone asked, and what to do next.
 * A red "never" on a 40-day-old debt is the single most useful thing this
 * table can tell you.
 */
function LastChasedCell({ invoice }: { invoice: InvoiceListItem }) {
  const { chase } = invoice;
  const follow = followUpLabel(chase, invoice.isLate, { short: true });
  const urgent = chase.needsChase;
  return (
    <div
      className="min-w-0"
      title={
        chase.count > 0
          ? `Chased ${chase.count}× · last on ${formatDate(chase.lastSentAt!)}`
          : chase.sent
            ? `Never chased. Sent to ${chase.sent.to ?? 'no recorded address'} on ${formatDate(
                chase.sent.at,
              )} — that is the only contact on this invoice.`
            : 'No reminder and no delivery record on this invoice'
      }
    >
      {chase.count > 0 ? (
        <span className="num whitespace-nowrap text-secondary">
          {agoLabel(chase.daysSince ?? 0)}
          {chase.count > 1 && <span className="ml-1 text-caption text-grey-400">×{chase.count}</span>}
        </span>
      ) : chase.sent ? (
        // Never chased — but it did go out, and when is the thing you need
        // before writing the first chase.
        <span className="num whitespace-nowrap font-semibold text-status-partial">
          {sentLabel(chase.sent, { short: true })}
        </span>
      ) : (
        <span className="font-bold text-status-late">never</span>
      )}
      {follow && (
        <span
          className={cn(
            'num block whitespace-nowrap text-caption',
            urgent ? 'font-semibold text-status-late' : 'text-grey-500',
          )}
        >
          {follow}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile row — the table's columns collapse below 640px, so phones get cards
// ---------------------------------------------------------------------------

function DueChip({ invoice }: { invoice: InvoiceListItem }) {
  if (invoice.state === 'PAID') {
    return (
      <span className="num text-caption text-grey-500">Paid {formatDate(invoice.paidDate ?? invoice.dueDate)}</span>
    );
  }
  if (invoice.isLate) {
    return (
      <span className="num inline-flex items-center rounded-full bg-status-late-bg px-2 py-0.5 text-caption font-bold text-status-late">
        {invoice.daysOverdue}d overdue
      </span>
    );
  }
  return <span className="num text-caption text-grey-500">Due {formatDate(invoice.dueDate)}</span>;
}

/**
 * A draft's badge: "NOT SENT", or "SENDS OCT 20" when it is scheduled to send
 * itself — the one thing about a scheduled draft the list must not hide.
 */
function draftPillLabel(invoice: InvoiceListItem, timeZone: string | undefined): string {
  return invoice.scheduledSendAt
    ? `SENDS ${formatScheduledShort(invoice.scheduledSendAt, timeZone).toUpperCase()}`
    : 'NOT SENT';
}

function MobileInvoiceCard({
  invoice,
  selected,
  onSelect,
  onClick,
  onRemind,
  onRecordPayment,
  onMarkSent,
}: {
  invoice: InvoiceListItem;
  selected: boolean;
  onSelect: (checked: boolean) => void;
  onClick: () => void;
  onRemind: () => void;
  onRecordPayment: () => void;
  onMarkSent: () => void;
}) {
  const timeZone = useDeliveryStatus().data?.timeZone;
  const partial = invoice.paidAmount > 0 && invoice.dueAmount > 0;
  const chased = chaseLabel(invoice.chase, invoice.isLate);
  return (
    <div
      className={cn(
        'flex items-start border-b border-grey-100 last:border-b-0',
        selected && 'bg-accent-50/60',
        // A draft is not owed yet — dimmed, like its desktop row.
        invoice.state === 'DRAFT' && !selected && 'bg-grey-50/60 opacity-60',
      )}
    >
    {/* Selection reaches the phone too — the chase queue is exactly the thing
        you work from a sofa. Outside the card button: a checkbox inside a
        button is not a control. */}
    <span className="py-4 pl-4">
      <RowCheckbox
        checked={selected}
        onToggle={(checked) => onSelect(checked)}
        label={`Select invoice #${invoice.number}`}
      />
    </span>
    <span className="min-w-0 flex-1">
    <button
      type="button"
      onClick={onClick}
      className="block w-full px-3 py-3 text-left transition-colors active:bg-grey-50"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="num text-caption font-bold text-accent-700">#{invoice.number}</span>
            <StatusPill
              status={invoiceStatusToPill(invoice.state, invoice.isLate)}
              label={
                invoice.state === 'DRAFT'
                  ? draftPillLabel(invoice, timeZone)
                  : invoice.isLate && invoice.state === 'PARTIAL'
                    ? 'LATE · PARTIAL'
                    : undefined
              }
            />
          </span>
          <span className="mt-1 block truncate text-body-sm font-semibold text-secondary">
            {invoice.client.name}
          </span>
          {invoice.subject && (
            <span className="mt-0.5 block truncate text-caption text-grey-500">{invoice.subject}</span>
          )}
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <DueChip invoice={invoice} />
            {/* Who has already been chased — the phone gets the same answer
                the desktop LAST CHASED column gives. */}
            {chased && (
              <span
                className={cn(
                  'num text-caption',
                  invoice.chase.count === 0
                    ? 'font-semibold text-status-late'
                    : invoice.chase.dueForFollowUp
                      ? 'font-semibold text-secondary'
                      : 'text-grey-500',
                )}
              >
                {chased}
              </span>
            )}
            {/* …and whether it ever went out, which the chase line cannot say. */}
            {invoice.chase.count === 0 && invoice.chase.sent && (
              <span className="num text-caption text-status-partial">
                {sentLabel(invoice.chase.sent, { short: true })}
              </span>
            )}
          </span>
        </span>
        <span className="shrink-0 text-right">
          <span
            className={cn(
              'num block text-body font-bold',
              invoice.dueAmount > 0
                ? invoice.isLate
                  ? 'text-status-late'
                  : 'text-secondary'
                : 'text-grey-400',
            )}
          >
            {invoice.state === 'DRAFT'
              ? formatMoney(invoice.amount)
              : invoice.dueAmount > 0
                ? formatMoney(invoice.dueAmount)
                : '—'}
          </span>
          <span className="num mt-0.5 block text-caption text-grey-500">
            {invoice.state === 'DRAFT' ? 'not sent' : invoice.dueAmount > 0 ? 'due' : 'settled'}
          </span>
          {partial && (
            <span className="num mt-0.5 block text-caption text-grey-500">
              of {formatMoney(invoice.amount)}
            </span>
          )}
        </span>
      </span>
    </button>
    {/* The desktop row's quick actions, which the card used to drop — chasing
        and recording a payment are exactly what you do from a phone. Outside
        the card button, because a button inside a button is not a control. */}
    {(invoice.isLate || invoice.state === 'OPEN' || invoice.state === 'PARTIAL' || invoice.state === 'DRAFT') && (
      <div className="flex flex-wrap gap-2 px-3 pb-3">
        {invoice.isLate && (
          <button
            type="button"
            onClick={onRemind}
            className="inline-flex items-center gap-1.5 rounded-lg border border-status-late-border bg-status-late-bg px-2.5 py-1.5 text-caption font-semibold text-status-late active:opacity-80"
          >
            <BellRing className="h-3.5 w-3.5" aria-hidden />
            {invoice.chase.count === 0 ? 'Chase' : 'Chase again'}
          </button>
        )}
        {(invoice.state === 'OPEN' || invoice.state === 'PARTIAL') && (
          <button
            type="button"
            onClick={onRecordPayment}
            className="inline-flex items-center gap-1.5 rounded-lg border border-grey-200 px-2.5 py-1.5 text-caption font-semibold text-grey-600 active:bg-grey-50"
          >
            <CreditCard className="h-3.5 w-3.5" aria-hidden />
            Record payment
          </button>
        )}
        {invoice.state === 'DRAFT' && (
          <button
            type="button"
            onClick={onMarkSent}
            className="inline-flex items-center gap-1.5 rounded-lg border border-grey-200 px-2.5 py-1.5 text-caption font-semibold text-grey-600 active:bg-grey-50"
          >
            <Send className="h-3.5 w-3.5" aria-hidden />
            Mark as sent
          </button>
        )}
      </div>
    )}
    </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row actions — the two things you do to an invoice without needing to read it
// ---------------------------------------------------------------------------

function RowAction({
  label,
  onClick,
  children,
  tone,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  tone?: 'late';
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={cn(
        'rounded-lg p-1.5 transition-colors',
        tone === 'late'
          ? 'text-status-late hover:bg-status-late-bg'
          : 'text-grey-400 hover:bg-accent-50 hover:text-accent-700',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function InvoicesListPage() {
  const navigate = useNavigate();
  // Filters live in the URL: /invoices?state=DRAFT is a real, bookmarkable,
  // Slackable view, and Back undoes a filter instead of leaving the page.
  const [searchParams, setSearchParams] = useSearchParams();
  // The "← Invoices" link on an invoice comes back to THIS view, not the default.
  const viewQuery = searchParams.toString();
  useEffect(() => rememberInvoiceListView(viewQuery), [viewQuery]);
  const searchRef = useRef<HTMLInputElement>(null);
  // Wraps the rows so j/k can walk the invoice links inside it.
  const tableRef = useRef<HTMLDivElement>(null);

  const rawState = (searchParams.get('state') ?? '').toUpperCase();
  // No `state` in the URL is not "no filter" — it is the default work queue.
  const stateTab: StateTab = VALID_STATES.has(rawState) ? (rawState as StateTab) : DEFAULT_TAB;
  const clientId = searchParams.get('client') ?? '';
  const search = searchParams.get('q') ?? '';
  const focus = parseFocus(searchParams.get('focus'));
  const urlSort = searchParams.get('sort');
  const sort = useMemo<DataTableSort>(() => {
    if (urlSort) {
      const [key, dir] = urlSort.split(':');
      if (key && (dir === 'asc' || dir === 'desc')) return { key, dir };
    }
    return defaultSortFor(stateTab, focus);
    // focus is re-parsed each render; key on its serialized form instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSort, stateTab, searchParams.get('focus')]);
  const page = Math.max(0, Number(searchParams.get('page') ?? '1') - 1) || 0;

  /** Patch the query string; `null` removes a key. Page resets unless given. */
  const patchParams = useCallback(
    (patch: Record<string, string | null>, opts: { replace?: boolean } = {}) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          if (!('page' in patch)) next.delete('page');
          return next;
        },
        { replace: opts.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const summary = useInvoiceSummary();
  /**
   * A failed KPI query must SAY SO. `summary` and `unbilled` are separate
   * requests from the table's, so the table's own error branch cannot speak
   * for them — see the note on `KpiTile`.
   */
  const summaryFailed = {
    error: summary.isError,
    errorMessage: summary.error instanceof Error ? summary.error.message : undefined,
    onRetry: () => void summary.refetch(),
  };
  const clients = useInvoiceClients();
  // 'ALL' is a tab, not a server filter — the API's "no state" means the same.
  // The Open tab is everything not yet paid, drafts included (Jesse, 2026-10-01).
  const invoices = useInvoices({
    state: stateTab === 'ALL' ? '' : stateTab === 'OPEN' ? 'UNPAID' : stateTab,
    clientId,
    search,
  });
  const unbilled = useUnbilled();
  const unbilledFailed = {
    error: unbilled.isError,
    errorMessage: unbilled.error instanceof Error ? unbilled.error.message : undefined,
    onRetry: () => void unbilled.refetch(),
  };
  // Recording a payment is the single most common thing you do from this list;
  // it no longer costs a round trip through the detail page.
  const [payTarget, setPayTarget] = useState<InvoiceListItem | null>(null);
  // Phones only: the KPI/aging block is collapsed by default (see below).
  const [showSummary, setShowSummary] = useState(false);
  // Ticked rows, by id. The queue is worked from here: five overdue invoices
  // are one selection and five screens, not five round trips through a detail
  // page. Anchored for shift-click ranges.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const anchorId = useRef<string | null>(null);


  // Changing the view drops any explicit sort so the new view gets its own
  // default (Late leads with the most overdue; everything else newest-first).
  // The default view keeps a clean URL (/invoices), every other tab names
  // itself in the query string so the view stays shareable.
  const setStateFilter = (value: string) => {
    // Recurring is the sibling screen, not a state — the tab navigates.
    if (value === RECURRING_TAB) {
      navigate('/invoices/recurring');
      return;
    }
    patchParams({ state: value === DEFAULT_TAB ? null : value, focus: null, sort: null });
  };
  const setFocusFilter = (next: Focus) =>
    patchParams({ focus: serializeFocus(next), state: null, sort: null });
  const setPage = (updater: (p: number) => number) =>
    patchParams({ page: String(updater(page) + 1) });

  const leadWithDue = isLateView(stateTab, focus);

  /**
   * Which subjects say nothing on THIS view — computed over the unsorted,
   * unpaged rows so the answer does not change as you page or re-sort.
   */
  const boilerplate = useMemo(
    () => boilerplateSubjects((invoices.data ?? []).filter((r) => matchesFocus(r, focus))),
    [invoices.data, focus],
  );

  const timeZone = useDeliveryStatus().data?.timeZone;
  const columns = useMemo<Array<DataTableColumn<InvoiceListItem>>>(() => {
    const base: Array<DataTableColumn<InvoiceListItem>> = [
      {
        key: 'number',
        header: 'Invoice',
        sortable: true,
        sortValue: (r) => invoiceNumberSortKey(r.number),
        render: (r) => (
          <Link
            to={`/invoices/${r.id}`}
            // The keyboard walker (j/k) moves focus between these links, and
            // `x` reads the id off whichever one is focused.
            data-invoice-id={r.id}
            onClick={(e) => e.stopPropagation()}
            className="rounded font-semibold text-accent-700 hover:underline num focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          >
            #{r.number}
          </Link>
        ),
        className: 'w-20',
      },
      {
        key: 'client',
        header: 'Client / subject',
        sortable: true,
        sortValue: (r) => r.client.name,
        render: (r) => (
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 truncate font-medium text-secondary">
              <span className="truncate">{r.client.name}</span>
              {/* Written by a recurring schedule, not typed by a human — the
                  one thing to know before editing a draft that will be
                  recreated next month if you delete it. */}
              {r.recurring && (
                <span
                  className="shrink-0 text-accent"
                  title={`From the recurring schedule “${r.recurring.templateSubject}”`}
                  aria-label="From a recurring schedule"
                  role="img"
                >
                  <Repeat className="h-3.5 w-3.5" aria-hidden />
                </span>
              )}
            </p>
            <SubjectLine row={r} boilerplate={boilerplate} showChase={!leadWithDue} />
          </div>
        ),
        // Cap keeps the row-action column on screen: min-content of the capped
        // cell sets the table's minimum width (see CLAUDE.md). The selection
        // column costs ~40px, so this gives it back.
        className: 'max-w-[13rem]',
      },
      {
        key: 'issueDate',
        header: 'Issued',
        sortable: true,
        sortValue: (r) => r.issueDate,
        render: (r) => <span className="num whitespace-nowrap">{formatDate(r.issueDate)}</span>,
        className: 'hidden lg:table-cell whitespace-nowrap',
      },
      {
        key: 'dueDate',
        header: 'Due',
        sortable: true,
        sortValue: (r) => r.dueDate,
        render: (r) => (
          <span className={cn('num whitespace-nowrap', r.isLate && 'font-semibold text-status-late')}>
            {formatDate(r.dueDate)}
            {r.isLate && <span className="ml-1.5 text-caption">({r.daysOverdue}d)</span>}
          </span>
        ),
        className: 'whitespace-nowrap',
      },
      {
        key: 'amount',
        header: 'Amount',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.amount,
        render: (r) => (
          <>
            <span className="whitespace-nowrap">{formatMoney(r.amount)}</span>
            {r.paidAmount > 0 && r.state !== 'PAID' && (
              <span className="block whitespace-nowrap text-caption text-success">
                {formatMoney(r.paidAmount)} paid
              </span>
            )}
          </>
        ),
        className: 'hidden md:table-cell whitespace-nowrap',
      },
      {
        key: 'dueAmount',
        header: 'Amount due',
        align: 'right',
        sortable: true,
        sortValue: (r) => r.dueAmount,
        render: (r) =>
          r.state === 'DRAFT' ? (
            // A draft is owed by nobody until it is sent.
            <span className="whitespace-nowrap text-caption font-semibold text-grey-500">not sent</span>
          ) : r.dueAmount > 0 ? (
            <>
              <span className={cn('whitespace-nowrap', r.isLate && 'font-semibold text-status-late')}>
                {formatMoney(r.dueAmount)}
              </span>
              {/* On a late view the Amount column steps aside (it repeats this
                  figure on every unpaid invoice), so the partial-payment
                  credit travels with the balance it explains. */}
              {leadWithDue && r.paidAmount > 0 && r.state !== 'PAID' && (
                <span className="block whitespace-nowrap text-caption text-success">
                  {formatMoney(r.paidAmount)} paid of {formatMoney(r.amount)}
                </span>
              )}
            </>
          ) : (
            <span className="text-grey-400">—</span>
          ),
        className: 'whitespace-nowrap',
      },
      {
        key: 'state',
        header: 'Status',
        align: 'center',
        sortable: true,
        sortValue: (r) => (r.isLate ? 'LATE' : r.state),
        render: (r) => (
          // LATE is derived client-side from isLate — never stored. A partly-paid
          // late invoice says so on the badge instead of hiding it in a column diff.
          <StatusPill
            status={invoiceStatusToPill(r.state, r.isLate)}
            label={r.state === 'DRAFT' ? draftPillLabel(r, timeZone) : r.isLate && r.state === 'PARTIAL' ? 'LATE · PARTIAL' : undefined}
          />
        ),
        className: 'w-32',
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <div className="flex items-center justify-end gap-0.5">
            {r.isLate && (
              <RowAction
                label={`Send reminder for #${r.number}`}
                tone="late"
                onClick={() => navigate(`/invoices/${r.id}?remind=1`)}
              >
                <BellRing className="h-4 w-4" aria-hidden />
              </RowAction>
            )}
            {(r.state === 'OPEN' || r.state === 'PARTIAL') && (
              <RowAction
                label={`Record a payment on #${r.number}`}
                onClick={() => setPayTarget(r)}
              >
                <CreditCard className="h-4 w-4" aria-hidden />
              </RowAction>
            )}
            {r.state === 'DRAFT' && (
              // Sending opens the composer on the invoice — the list must not
              // flip a draft to open without recording who it went to.
              <RowAction
                label={`Send #${r.number} to ${r.client.name}`}
                onClick={() => navigate(`/invoices/${r.id}?send=1`)}
              >
                <Send className="h-4 w-4" aria-hidden />
              </RowAction>
            )}
          </div>
        ),
        className: 'w-20 px-1',
      },
    ];
    if (!leadWithDue) return base;
    // On a late view the money you are chasing leads: Amount due moves ahead
    // of the issued/due dates rather than sitting last before Status. Amount
    // itself leaves — it repeats Amount due on every unpaid invoice, and the
    // space buys the chase state its column.
    const dueAmountIndex = base.findIndex((c) => c.key === 'dueAmount');
    const [dueAmountCol] = base.splice(dueAmountIndex, 1);
    base.splice(base.findIndex((c) => c.key === 'amount'), 1);
    // Issued goes too: when you are chasing, the date that matters is the one
    // the money was due — and "(251d)" already says how old this is.
    base.splice(base.findIndex((c) => c.key === 'issueDate'), 1);
    base.splice(2, 0, dueAmountCol);
    // …and the chase state sits next to the days-overdue it answers: "251 days
    // overdue, never chased" is a different problem from "251 days overdue,
    // chased on Monday".
    base.splice(base.findIndex((c) => c.key === 'dueDate') + 1, 0, {
      key: 'lastChased',
      header: 'Last chased',
      sortable: true,
      // Never-chased sorts as the stalest possible chase, so one click on this
      // header puts the invoices nobody has asked about at the top.
      sortValue: (r) =>
        r.chase.count > 0
          ? (r.chase.daysSince ?? 0)
          : // Never chased sorts as the stalest possible contact; a send with
            // no chase after it sorts just behind that, by its own age.
            Number.MAX_SAFE_INTEGER - (r.chase.sent ? 1 : 0),
      render: (r) => <LastChasedCell invoice={r} />,
      className: 'hidden md:table-cell w-32 whitespace-nowrap',
    });
    return base;
  }, [leadWithDue, navigate, boilerplate, timeZone]);

  const rows = useMemo(
    () => (invoices.data ?? []).filter((r) => matchesFocus(r, focus)),
    [invoices.data, focus],
  );

  /**
   * The chase strip counts LATE invoices, so it belongs only on views that can
   * contain one. Sitting above two drafts it read as if those drafts were
   * overdue.
   */
  const chaseStripApplies =
    Boolean(focus) || stateTab === 'OPEN' || stateTab === 'LATE' || stateTab === 'ALL';

  // The table's own comparator, not a second copy of it: this page slices the
  // rows into pages before handing them over, so the slice and the `<tbody>`
  // have to agree about the order — including where the blanks go.
  const sortedRows = useMemo(() => sortRows(rows, columns, sort), [rows, sort, columns]);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = sortedRows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const totals = useMemo(
    () =>
      sortedRows.reduce(
        (acc, r) => ({
          amount: acc.amount + r.amount,
          // Drafts are owed by nobody until they are sent, so the Amount due
          // footing leaves them out and the note under the table names them.
          due: acc.due + (r.state === 'DRAFT' ? 0 : r.dueAmount),
          draftDue: acc.draftDue + (r.state === 'DRAFT' ? r.dueAmount : 0),
          draftCount: acc.draftCount + (r.state === 'DRAFT' ? 1 : 0),
        }),
        { amount: 0, due: 0, draftDue: 0, draftCount: 0 },
      ),
    [sortedRows],
  );

  const chaseCounts = summary.data?.chase;

  /**
   * How old the oldest unbilled work is, in days. The Unbilled tile claimed
   * "$934,719 · Ready to invoice · 14 clients" while a quarter of it was work
   * from January 2025 — a readiness the data does not support.
   */
  const oldestUnbilledDays = useMemo(() => {
    const dates = (unbilled.data?.clients ?? [])
      .map((c) => c.oldestEntryDate)
      .filter((d): d is string => Boolean(d));
    if (dates.length === 0) return null;
    const oldest = dates.reduce((a, b) => (a < b ? a : b));
    return Math.max(0, Math.round((Date.now() - Date.parse(`${oldest}T12:00:00`)) / 86_400_000));
  }, [unbilled.data]);

  // ---- selection -----------------------------------------------------------
  //
  // Selection is scoped to what is on screen: change the filter and a tick you
  // can no longer see is dropped, so a bulk action can never reach an invoice
  // the current view does not contain.
  useEffect(() => {
    const visible = new Set(sortedRows.map((r) => r.id));
    setSelectedIds((prev) => {
      const kept = prev.filter((id) => visible.has(id));
      return kept.length === prev.length ? prev : kept;
    });
  }, [sortedRows]);

  const selectedRows = useMemo(
    () => sortedRows.filter((r) => selected.has(r.id)),
    [sortedRows, selected],
  );

  const toggleRow = useCallback(
    (row: InvoiceListItem, checked: boolean, shiftKey: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        // Shift-click fills the range from the last box you touched — ticking
        // 14 overdue rows should not cost 14 clicks.
        const anchorIndex = anchorId.current
          ? sortedRows.findIndex((r) => r.id === anchorId.current)
          : -1;
        const rowIndex = sortedRows.findIndex((r) => r.id === row.id);
        if (shiftKey && anchorIndex >= 0 && rowIndex >= 0) {
          const [from, to] = anchorIndex < rowIndex ? [anchorIndex, rowIndex] : [rowIndex, anchorIndex];
          for (const r of sortedRows.slice(from, to + 1)) {
            if (checked) next.add(r.id);
            else next.delete(r.id);
          }
        } else if (checked) {
          next.add(row.id);
        } else {
          next.delete(row.id);
        }
        return sortedRows.filter((r) => next.has(r.id)).map((r) => r.id);
      });
      anchorId.current = row.id;
    },
    [sortedRows],
  );

  const allSelected = sortedRows.length > 0 && selectedRows.length === sortedRows.length;

  /**
   * Keyboard: the queue is worked without the mouse.
   *
   *   /            focus search
   *   j / k        move down / up the rows (arrows too, once a row has focus)
   *   x            tick or untick the focused row
   *   a            select every row in this view, or clear it
   *   Enter        open the focused row (the row IS a link)
   *   n            new invoice
   *   Esc          clear the selection
   */
  useEffect(() => {
    const rowLinks = () =>
      Array.from(
        tableRef.current?.querySelectorAll<HTMLAnchorElement>('a[data-invoice-id]') ?? [],
      );
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // A dialog owns the keyboard while it is open — "n" must not navigate a
      // half-written chase away to the new-invoice page.
      if (document.querySelector('[role="dialog"],[role="alertdialog"]')) return;
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      const typing =
        tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || Boolean(el?.isContentEditable);
      if (typing) {
        // Escape gets you out of the search box and back to the rows.
        if (e.key === 'Escape') (el as HTMLElement).blur();
        return;
      }
      if (e.key === '/') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
        return;
      }
      if (e.key === 'Escape') {
        setSelectedIds([]);
        return;
      }
      if (e.key === 'n') {
        e.preventDefault();
        navigate('/invoices/new');
        return;
      }
      const links = rowLinks();
      if (links.length === 0) return;
      const current = links.findIndex((l) => l === document.activeElement);
      const focusAt = (i: number) => {
        e.preventDefault();
        links[Math.max(0, Math.min(links.length - 1, i))]?.focus();
      };
      if (e.key === 'j') return focusAt(current < 0 ? 0 : current + 1);
      if (e.key === 'k') return focusAt(current < 0 ? 0 : current - 1);
      // Arrows only take over once the user is already walking the rows —
      // otherwise they would stop the page scrolling.
      if (current >= 0 && e.key === 'ArrowDown') return focusAt(current + 1);
      if (current >= 0 && e.key === 'ArrowUp') return focusAt(current - 1);
      if (e.key === 'a') {
        e.preventDefault();
        setSelectedIds((prev) =>
          prev.length === sortedRows.length ? [] : sortedRows.map((r) => r.id),
        );
        return;
      }
      if (e.key === 'x' && current >= 0) {
        e.preventDefault();
        const id = links[current].dataset.invoiceId;
        const row = pageRows.find((r) => r.id === id);
        if (row) toggleRow(row, !selected.has(row.id), e.shiftKey);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, pageRows, selected, sortedRows, toggleRow]);

  /**
   * The table's own columns, led by the selection box. It is deliberately NOT
   * part of `columns`: that memo feeds the sort comparator, and re-deriving the
   * sort on every tick would be work for nothing.
   *
   * The header box is scoped to the current filter — it selects every row this
   * view contains, across pages, never the whole ledger.
   */
  const tableColumns = useMemo<Array<DataTableColumn<InvoiceListItem>>>(
    () => [
      {
        key: 'select',
        header: (
          <RowCheckbox
            checked={allSelected}
            indeterminate={selectedRows.length > 0}
            onToggle={(checked) => setSelectedIds(checked ? sortedRows.map((r) => r.id) : [])}
            label={
              allSelected
                ? `Clear the selection of ${sortedRows.length} invoices`
                : `Select all ${sortedRows.length} invoices in this view`
            }
          />
        ),
        render: (r) => (
          <RowCheckbox
            checked={selected.has(r.id)}
            onToggle={(checked, shiftKey) => toggleRow(r, checked, shiftKey)}
            label={`Select invoice #${r.number} — ${r.client.name}, ${formatMoney(r.dueAmount)} due`}
          />
        ),
        // Narrow on purpose: the row-action column at the far right is what
        // gets pushed off screen when a table's min-content grows (CLAUDE.md).
        className: 'w-8 pl-3 pr-1',
      },
      ...columns,
    ],
    [columns, sortedRows, selected, selectedRows.length, allSelected, toggleRow],
  );

  /**
   * The aging report a bookkeeper actually asks for — the rows currently on
   * screen, in the order they are on screen, chase state included. Given a
   * selection it exports exactly those rows instead.
   */
  const exportCsv = (only?: InvoiceListItem[]) => {
    const exportRows = only && only.length > 0 ? only : sortedRows;
    const exportTotals = exportRows.reduce(
      (acc, r) => ({ amount: acc.amount + r.amount, due: acc.due + r.dueAmount }),
      { amount: 0, due: 0 },
    );
    const view = only ? 'selected' : focus ? focusLabel(focus) : stateTab;
    downloadCsv(csvFilename({ entity: 'invoices', view }), [
      [
        'Invoice',
        'Client',
        'Subject',
        'PO number',
        'Issued',
        'Due',
        'Status',
        'Days overdue',
        'Amount',
        'Paid',
        'Amount due',
        'Reminders sent',
        'Last chased',
        'Next follow-up',
      ],
      ...exportRows.map((r) => [
        r.number,
        r.client.name,
        r.subject ?? '',
        r.poNumber ?? '',
        r.issueDate,
        r.dueDate,
        r.isLate ? `LATE · ${r.state}` : r.state,
        r.isLate ? r.daysOverdue : 0,
        csvMoney(r.amount),
        csvMoney(r.paidAmount),
        csvMoney(r.dueAmount),
        r.chase.count,
        r.chase.lastSentAt ? r.chase.lastSentAt.slice(0, 10) : r.isLate ? 'never' : '',
        r.isLate ? (r.chase.nextFollowUp ?? '') : '',
      ]),
      [],
      [
        `Total · ${exportRows.length} invoices`,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        csvMoney(exportTotals.amount),
        '',
        csvMoney(exportTotals.due),
      ],
    ]);
    toast.success(
      `${exportRows.length} invoice${exportRows.length === 1 ? '' : 's'} exported`,
    );
  };

  // The default view is a filter too — it just doesn't need saying in the URL.
  const hasFilters = Boolean(stateTab !== DEFAULT_TAB || clientId || search || focus);
  const clearAll = () =>
    setSearchParams(new URLSearchParams(), { replace: false });

  const emptyState = hasFilters ? (
    <EmptyState
      icon={<FileText />}
      title="No matching invoices"
      description="Try a different state, client, or search term."
      action={
        <Button variant="outline" onClick={clearAll}>
          Clear filters
        </Button>
      }
    />
  ) : (
    /* An empty default view means nobody owes you anything — which is good
       news, not an error, and must not read like "you have no invoices" when
       a year of paid ones sits on the next tab. */
    <EmptyState
      icon={<FileText />}
      title="Nothing outstanding"
      description={
        summary.data && summary.data.draftCount > 0
          ? `No invoice is waiting to be paid. ${summary.data.draftCount} draft${
              summary.data.draftCount === 1 ? '' : 's'
            } still to go out.`
          : 'No invoice is waiting to be paid right now.'
      }
      action={
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => navigate('/invoices/new')}>
            <Plus className="h-4 w-4" aria-hidden />
            New invoice
          </Button>
          <Button variant="outline" onClick={() => setStateFilter('ALL')}>
            Show every invoice
          </Button>
        </div>
      }
    />
  );

  return (
    <div>
      <PageHeader
        title="Invoices"
        subtitle="Money in — every invoice, who owes what, and how late it is."
        actions={
          <>
            {/* Above `sm` it belongs in the header, like /agreements and
                /clients. Below `sm` the header is already three buttons wide,
                so it moves into the filter bar — see the note there. It is
                never absent. */}
            <ExportCsvButton
              className="hidden sm:inline-flex"
              count={sortedRows.length}
              noun="invoices"
              size="md"
              onClick={() => exportCsv()}
            />
            <Button onClick={() => navigate('/invoices/new')}>
              <Plus className="h-4 w-4" aria-hidden />
              New invoice
            </Button>
          </>
        }
      />

      {/* Phones led with a full screen of KPI tiles and aging chrome before the
          first invoice. The two numbers that matter stay visible; the rest is
          one tap away, and the list starts above the fold. */}
      <div className="mb-3 sm:hidden">
        <button
          type="button"
          aria-expanded={showSummary}
          onClick={() => setShowSummary((v) => !v)}
          className="flex w-full items-center justify-between gap-3 rounded-xl border border-grey-200/50 bg-white px-3 py-2.5 text-left shadow-card"
        >
          <span className="min-w-0">
            <span className="block text-caption font-semibold uppercase tracking-wider text-grey-500">
              Outstanding
            </span>
            <span className="num block text-heading-3 text-secondary">
              {summary.data ? formatMoney(summary.data.outstanding, ROLLUP) : '—'}
            </span>
            {summary.data && (
              <span className="num block text-caption text-grey-500">
                <span className="font-semibold text-status-late">
                  {formatMoney(summary.data.overdue, ROLLUP)}
                </span>{' '}
                overdue · {summary.data.lateCount} late
                {summary.data.chase.neverChased > 0 && (
                  <> · {summary.data.chase.neverChased} never chased</>
                )}
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1 text-caption font-semibold text-accent-700">
            {showSummary ? 'Less' : 'KPIs'}
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', showSummary && 'rotate-180')}
              aria-hidden
            />
          </span>
        </button>
      </div>

      {/* Summary tiles + aging — every figure is a filter */}
      <div
        className={cn(
          'mb-4 grid-cols-2 gap-3 sm:!grid xl:grid-cols-4',
          showSummary ? 'grid' : 'hidden',
        )}
      >
        <KpiTile
          label="Outstanding"
          kind="ar"
          loading={summary.isLoading}
          {...summaryFailed}
          value={summary.data ? formatMoney(summary.data.outstanding, ROLLUP) : '—'}
          hint={summary.data ? `${summary.data.openCount} open invoices` : undefined}
          // Outstanding IS the default view, so the tile selects the tab
          // rather than laying a redundant client-side cross-cut over it.
          active={stateTab === 'OPEN' && !focus}
          onClick={() => setStateFilter(stateTab === 'OPEN' && !focus ? 'ALL' : 'OPEN')}
        />
        <KpiTile
          label="Overdue"
          kind="overdue"
          loading={summary.isLoading}
          {...summaryFailed}
          tone={summary.data && summary.data.overdue > 0 ? 'late' : undefined}
          value={summary.data ? formatMoney(summary.data.overdue, ROLLUP) : '—'}
          hint={summary.data ? `${summary.data.lateCount} late invoices` : undefined}
          active={stateTab === 'LATE'}
          onClick={() => setStateFilter(stateTab === 'LATE' ? DEFAULT_TAB : 'LATE')}
        />
        <KpiTile
          label="Draft"
          kind="draft"
          loading={summary.isLoading}
          {...summaryFailed}
          value={summary.data ? formatMoney(summary.data.draftTotal, ROLLUP) : '—'}
          // A draft has an AGE, and it is money nobody has asked for yet.
          // "2 awaiting send" never mentioned that one of them had been sitting
          // unsent for a week.
          hint={
            summary.data
              ? summary.data.draftCount > 0 && summary.data.draftOldestDays > 0
                ? `${summary.data.draftCount} awaiting send · oldest ${summary.data.draftOldestDays}d`
                : `${summary.data.draftCount} awaiting send`
              : undefined
          }
          tone={summary.data && summary.data.draftOldestDays >= 7 ? 'late' : undefined}
          active={stateTab === 'DRAFT'}
          onClick={() => setStateFilter(stateTab === 'DRAFT' ? DEFAULT_TAB : 'DRAFT')}
        />
        <KpiTile
          label="Unbilled"
          kind="unbilled"
          loading={unbilled.isLoading}
          {...unbilledFailed}
          value={unbilled.data ? formatMoney(unbilled.data.totals.amount, ROLLUP) : '—'}
          // ⚠ It is NOT all "ready to invoice". A quarter of this figure is
          // work from early 2025 that nobody is going to bill now; asserting
          // readiness the data does not support is how a tile becomes wallpaper.
          // Say what it is (unbilled billable work) and how old the oldest of
          // it is, and let the age argue.
          hint={
            unbilled.data
              ? `${unbilled.data.totals.clientCount} clients${
                  oldestUnbilledDays !== null ? ` · oldest work ${oldestUnbilledDays}d` : ''
                }`
              : undefined
          }
          tone={oldestUnbilledDays !== null && oldestUnbilledDays > 180 ? 'late' : undefined}
          to="/invoices/new"
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {/* Phones scroll the strip sideways in one row instead of wrapping to
            two — a whole row of chrome before the first invoice. */}
        <SegmentedControl
          ariaLabel="Invoice state filter"
          size="md"
          scrollable
          // A focus chip narrows the state tab; it does not replace it. Blanking
          // the strip left the filter model in a state it could not represent —
          // click an aging band and Open/Late/Partial/Draft/Paid/Void/All all
          // rendered inactive, so the view had no answer to "where am I?".
          value={stateTab as StateTab}
          onChange={setStateFilter}
          options={STRIP_TABS}
        />
        {/* One row on a phone, not two: the client picker and the search box
            each took a full 390px line above the first invoice. */}
        <div className="flex w-full items-center gap-2 sm:w-auto sm:flex-wrap">
          <Select
            aria-label="Filter by client"
            selectSize="sm"
            className="min-w-0 flex-1 sm:w-48 sm:flex-none"
            value={clientId}
            onChange={(e) => patchParams({ client: e.target.value })}
            options={[
              { value: '', label: 'All clients' },
              ...(clients.data ?? []).map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <SearchInput
            searches="invoices"
            ariaLabel="Search invoices by number or client"
            value={search}
            // Typing replaces rather than pushes: Back should undo a filter,
            // not walk backwards one character at a time.
            onChange={(next: string) => patchParams({ q: next }, { replace: true })}
            className="min-w-0 flex-1 sm:w-52 sm:flex-none"
          />
          {/* ⚠ EVERY LIST SCREEN EXPORTS — INCLUDING ON A PHONE. The button
              lives in the PageHeader above `sm`, where it belongs; below `sm`
              the header is already three buttons wide, so it moves down here
              into the filter bar instead of DISAPPEARING. A rule the app
              states about itself ("every list screen exports, all of them")
              cannot have a width at which it stops being true — and a person
              on a phone exports to send the file on, not to read it. */}
          <ExportCsvButton
            className="sm:hidden"
            count={sortedRows.length}
            noun="invoices"
            onClick={() => exportCsv()}
          />
        </div>
      </div>

      {/* Ticked rows take the strip's place: while a selection is live, what
          matters is what you are about to do to it. */}
      {selectedRows.length > 0 && (
        <BulkActionBar
          selected={selectedRows}
          totalInView={sortedRows.length}
          onClear={() => setSelectedIds([])}
          onExportCsv={(rows) => exportCsv(rows)}
        />
      )}

      {/* ONE STRIP: how late the money is, and who to chase. Two separate
          blocks — a full-width aging card and a chase row — used to sit here,
          and between them they pushed the first invoice to y=594 on a 900px
          screen. They answer the same morning question, so they share a line. */}
      <div className="mb-3 space-y-1.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <AgingStrip
            summary={summary.data}
            loading={summary.isLoading}
            focus={focus}
            lateOnly={stateTab === 'LATE' || focus?.kind === 'chase'}
            onSelect={(bucket) =>
              setFocusFilter(
                focus?.kind === 'aging' && focus.bucket === bucket
                  ? null
                  : { kind: 'aging', bucket },
              )
            }
          />
          {selectedRows.length === 0 &&
            chaseStripApplies &&
            chaseCounts &&
            chaseCounts.lateCount > 0 && (
              <>
                <span className="hidden h-4 w-px shrink-0 bg-grey-200 sm:block" aria-hidden />
                {/* Scrolls sideways in ONE row on a phone rather than wrapping
                    to two. Nothing is dropped — the whole cadence model is real
                    AR discipline and is the same on every screen — but it costs
                    40px of a phone instead of 100.
                    ⚠ AND IT SAYS SO. At 390px the strip ended mid-chip ("2 due
                    to follow u…") with nothing to suggest it moved, so the last
                    chip was unreachable to anyone who did not guess. `ScrollFade`
                    measures the overflow and draws an edge only while there is
                    something past it — a permanent gradient is a lie on a
                    desktop where the row fits. */}
                <ScrollFade className="-mx-1 w-full px-1 sm:mx-0 sm:w-auto sm:px-0">
                <div className="flex flex-nowrap items-center gap-1.5 sm:flex-wrap">
            {/* On the Late tab this chip restates the tab you are standing on,
                three inches under an Overdue tile printing the same money. It
                is a way IN to the late view, not a label for it. */}
            {stateTab !== 'LATE' && (
              <ChaseChip
                label="late"
                count={chaseCounts.lateCount}
                tone="late"
                active={false}
                title={`Show all ${chaseCounts.lateCount} overdue invoices`}
                onClick={() => setStateFilter('LATE')}
              />
            )}
            {/* The number the rows add up to, then its two halves. Two
                disjoint sub-queues used to be the only thing on screen, so
                five rows saying "chase this" sat under chips reading 3 and 2
                and nothing said five. When there is nothing to chase, three
                zeroes and two operators say it far worse than one sentence. */}
            {chaseCounts.needsChase === 0 ? (
              <span className="shrink-0 whitespace-nowrap rounded-full border border-status-paid/30 bg-status-paid/10 px-2.5 py-1 text-caption font-semibold text-status-paid">
                every late invoice chased
              </span>
            ) : (
              <>
                <ChaseChip
                  label="to chase today"
                  count={chaseCounts.needsChase}
                  tone="never"
                  active={focus?.kind === 'chase' && focus.queue === 'today'}
                  title={`${chaseCounts.needsChase} overdue invoices want an email today — ${chaseCounts.neverChased} never chased plus ${chaseCounts.dueForFollowUp} due to follow up`}
                  onClick={() =>
                    setFocusFilter(
                      focus?.kind === 'chase' && focus.queue === 'today'
                        ? null
                        : { kind: 'chase', queue: 'today' },
                    )
                  }
                />
                <span className="shrink-0 text-caption text-grey-400" aria-hidden>
                  =
                </span>
                <ChaseChip
                  label="never chased"
                  count={chaseCounts.neverChased}
                  tone="never"
                  active={focus?.kind === 'chase' && focus.queue === 'never'}
                  title="Overdue invoices with no reminder ever logged"
                  onClick={() =>
                    setFocusFilter(
                      focus?.kind === 'chase' && focus.queue === 'never'
                        ? null
                        : { kind: 'chase', queue: 'never' },
                    )
                  }
                />
                <span className="shrink-0 text-caption text-grey-400" aria-hidden>
                  +
                </span>
                <ChaseChip
                  label="due to follow up"
                  count={chaseCounts.dueForFollowUp}
                  tone="due"
                  active={focus?.kind === 'chase' && focus.queue === 'due'}
                  title={`Chased more than ${chaseCounts.cadenceDays} days ago and still unpaid`}
                  onClick={() =>
                    setFocusFilter(
                      focus?.kind === 'chase' && focus.queue === 'due'
                        ? null
                        : { kind: 'chase', queue: 'due' },
                    )
                  }
                />
              </>
            )}
                  {/* ⚠ "chase cadence 7 days" used to be printed here as a
                      fact nobody could act on. It is now the ChaseReminderBar's
                      sentence one line below — where it is attached to the
                      schedule that obeys it and to the control that changes it.
                      Saying it in both places would be saying it twice. */}
                </div>
                </ScrollFade>
              </>
            )}
        </div>
        {/* THE APP CHASES BY ITSELF, and the screen that computes the queue is
            where it says so. Rendered whenever chasing is the question this
            view is about — not only when something happens to be overdue,
            because "nothing is due a chase" is the answer on a good morning and
            an empty space is not. */}
        {selectedRows.length === 0 && <DepositsNotice />}
        {selectedRows.length === 0 && chaseStripApplies && <ChaseReminderBar />}
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <AgingBasisNote />
          {leadWithDue && !urlSort && selectedRows.length === 0 && (
            <p className="hidden text-caption text-grey-500 sm:block">
              Sorted most overdue first — the oldest debt is at the top.
            </p>
          )}
        </div>
      </div>

      {focus && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-caption font-bold uppercase tracking-wider text-white">
            {focusLabel(focus)}
            <button
              type="button"
              aria-label="Clear aging filter"
              onClick={() => setFocusFilter(null)}
              className="rounded-full p-0.5 transition-colors hover:bg-white/20"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
          <span className="num text-caption text-grey-500">
            {sortedRows.length} invoice{sortedRows.length === 1 ? '' : 's'} ·{' '}
            {formatMoney(totals.due, ROLLUP)} due
          </span>
        </div>
      )}

      {/* Table (desktop) / cards (mobile) */}
      <Card>
        {invoices.isLoading ? (
          <SkeletonTable rows={8} />
        ) : invoices.isError ? (
          // The server's own words are evidence, not the headline: a 500 whose
          // body reads "boom" is not a sentence to hand a user.
          (() => {
            const problem = describeError(invoices.error, 'the invoice list');
            return (
              <EmptyState
                icon={<AlertTriangle />}
                title={problem.title}
                description={problem.description}
                action={
                  <div className="flex flex-col items-center gap-3">
                    <Button onClick={() => invoices.refetch()} loading={invoices.isFetching}>
                      Try again
                    </Button>
                    {problem.detail && (
                      <p className="num text-caption text-grey-400">Server said: {problem.detail}</p>
                    )}
                  </div>
                }
              />
            );
          })()
        ) : pageRows.length === 0 ? (
          emptyState
        ) : (
          <>
            <div className="sm:hidden">
              {/* "SELECT ALL 7" no longer sits above the first invoice on a
                  phone. Selection still works per card, and the moment one row
                  is ticked the bar above offers the whole-view toggle — so the
                  affordance appears when it is wanted instead of costing 44px
                  of every phone screen. */}
              {selectedRows.length > 0 && (
                <div className="flex items-center gap-3 border-b border-grey-100 bg-grey-50 px-4 py-2">
                  <RowCheckbox
                    checked={allSelected}
                    indeterminate={selectedRows.length > 0}
                    onToggle={(checked) =>
                      setSelectedIds(checked ? sortedRows.map((r) => r.id) : [])
                    }
                    label={
                      allSelected
                        ? `Clear the selection of ${sortedRows.length} invoices`
                        : `Select all ${sortedRows.length} invoices in this view`
                    }
                  />
                  <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
                    {`${selectedRows.length} of ${sortedRows.length} selected`}
                  </span>
                </div>
              )}
              {pageRows.map((r) => (
                <MobileInvoiceCard
                  key={r.id}
                  invoice={r}
                  selected={selected.has(r.id)}
                  onSelect={(checked) => toggleRow(r, checked, false)}
                  onClick={() => navigate(`/invoices/${r.id}`)}
                  onRemind={() => navigate(`/invoices/${r.id}?remind=1`)}
                  onRecordPayment={() => setPayTarget(r)}
                  onMarkSent={() => navigate(`/invoices/${r.id}?send=1`)}
                />
              ))}
              <div className="flex items-center justify-between gap-2 border-t border-grey-200 bg-grey-50 px-4 py-2.5">
                <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
                  {sortedRows.length} shown
                </span>
                <span className="num text-body-sm font-bold text-secondary">
                  {formatMoney(totals.due, ROLLUP)} due
                </span>
              </div>
            </div>
            <div className="hidden sm:block" ref={tableRef}>
              <DataTable
                columns={tableColumns}
                data={pageRows}
                rowKey={(r) => r.id}
                // A draft is not owed yet: dimmed so the open and late money
                // reads first (Jesse, 2026-10-01), full strength on hover.
                rowClassName={(r) =>
                  r.state === 'DRAFT'
                    ? 'bg-grey-50/60 [&>td]:opacity-50 hover:[&>td]:opacity-100 focus-within:[&>td]:opacity-100'
                    : undefined
                }
                onRowClick={(r) => navigate(`/invoices/${r.id}`)}
                sort={sort}
                onSortChange={(next) => patchParams({ sort: serializeSort(next, sort) })}
                footer={
                  // Built from `columns` so the totals stay under their own
                  // headers when the late view reorders them.
                  <tr className="border-t-2 border-grey-200">
                    {tableColumns.map((col) => (
                      <td
                        key={col.key}
                        // A rollup renders whole dollars everywhere in this app
                        // (see ROLLUP above) — which means the column of cents
                        // above it does not visibly foot. The exact figure is
                        // one hover away rather than absent.
                        title={
                          col.key === 'amount'
                            ? `Exactly ${formatMoney(totals.amount)} — rounded to whole dollars, like every rollup on this page`
                            : col.key === 'dueAmount'
                              ? `Exactly ${formatMoney(totals.due)} — rounded to whole dollars, like every rollup on this page`
                              : undefined
                        }
                        className={cn(
                          'px-4 py-2.5',
                          col.className,
                          col.align === 'right' && 'num-cell text-secondary',
                        )}
                      >
                        {col.key === 'client' ? (
                          <span className="text-caption font-bold uppercase tracking-wider text-grey-500">
                            Total · {sortedRows.length} invoice{sortedRows.length === 1 ? '' : 's'}
                          </span>
                        ) : col.key === 'amount' ? (
                          // A total across a set is whole dollars — the same
                          // figure the Outstanding tile above prints. Rows keep
                          // their cents; somebody pays those exactly.
                          formatMoney(totals.amount, ROLLUP)
                        ) : col.key === 'dueAmount' ? (
                          formatMoney(totals.due, ROLLUP)
                        ) : null}
                      </td>
                    ))}
                  </tr>
                }
              />
            </div>
            {/* Drafts are in the Amount total but not in Amount due: nobody
                owes an invoice that has not been sent. Say so, with the figure. */}
            {totals.draftCount > 0 && (
              <p className="border-t border-grey-100 px-4 py-2 text-caption text-grey-500">
                {totals.draftCount} draft{totals.draftCount === 1 ? '' : 's'} not sent yet ·{' '}
                <span className="num font-semibold text-secondary">
                  {formatMoney(totals.draftDue, ROLLUP)}
                </span>{' '}
                — in Amount, not in Amount due.
              </p>
            )}
          </>
        )}
        {pageCount > 1 && (
          <div className="flex items-center justify-between gap-3 border-t border-grey-200 px-4 py-2.5">
            <p className="num text-caption text-grey-500">
              Showing {safePage * PAGE_SIZE + 1}–{safePage * PAGE_SIZE + pageRows.length} of{' '}
              {sortedRows.length}
            </p>
            <div className="flex items-center gap-1.5">
              <Button
                size="sm"
                variant="outline"
                disabled={safePage === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
                Prev
              </Button>
              <span className="num px-1 text-caption text-grey-500">
                {safePage + 1} / {pageCount}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              >
                Next
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* ⚠ **THE LEGEND HAS TO EXIST BEFORE THE SELECTION DOES.** The whole
          keyboard model — `/` focuses search, j/k walk the rows, x ticks one, a
          ticks all, Esc clears, n starts an invoice — was taught only inside
          the bulk action bar, which does not appear until you have already
          picked a row with the mouse. It was invisible to exactly the person it
          is for. It is one quiet line under the table now, on every visit, and
          it stands down while the bulk bar (which says the same thing in its
          own context) is up. */}
      {sortedRows.length > 0 && selectedRows.length === 0 && (
        <p className="mt-2 hidden text-caption text-grey-500 sm:block">
          Keyboard: <Key>/</Key> search · <Key>j</Key> <Key>k</Key> move · <Key>x</Key> tick a row ·{' '}
          <Key>a</Key> tick all · <Key>Esc</Key> clear · <Key>n</Key> new invoice
        </p>
      )}

      {payTarget && (
        <RecordPaymentModal
          invoice={payTarget}
          open
          onOpenChange={(open) => {
            if (!open) setPayTarget(null);
          }}
        />
      )}
    </div>
  );
}
